import { useState } from "react";
import { ButtonAdd, ButtonDelete, ButtonEdit } from "@/components/ui/button";
import AddTableModal from "./AddTableModal";
import {
  useDeleteTableMutation,
  useGetRestaurantTablesQuery,
  useCreateOrUpdateTableMutation,
} from "@/redux/services/table/table.api";
import { RestaurantTable } from "@/redux/services/table/table.type";
import { useModal, usePagination } from "@/lib/hooks";
import { useDelete } from "@/lib/useDelete";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import ReactPagination from "../Table/Pagination";
import { Skeleton } from "../ui/skeleton";
import Switch from "../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import toast from "react-hot-toast";
import { Armchair, Loader2 } from "lucide-react";
export function Delete({ item }: { item: RestaurantTable }) {
  const [deleteItem, { isSuccess, isError, error }] = useDeleteTableMutation();
  const onDelete = useDelete<RestaurantTable>({
    item,
    deleteItem,
    isSuccess,
    isError,
    error,
    successMessage: "Table supprimée",
  });
  return <ButtonDelete onClick={onDelete} />;
}

const ListeRestaurantTables = () => {
  const { limit, page, onChange } = usePagination(12);
  const [loadingTables, setLoadingTables] = useState<Record<string, boolean>>(
    {}
  );

  const { currentRestaurant: restaurant, currentUser } = useAppSelector((state) => state.user);
  const isReadOnly = currentUser?.roles?.includes(Roles.owner);

  const { data, isLoading } = useGetRestaurantTablesQuery({
    restaurantId: restaurant?.id as string,
    page,
    limit,
  });
  const { isOpen, item, closeModal, openEditModal, openModal } =
    useModal<RestaurantTable>();

  const [updateTable] = useCreateOrUpdateTableMutation();

  const handleToggleOrderPlacement = async (
    table: RestaurantTable,
    checked: boolean
  ) => {
    setLoadingTables((prev) => ({ ...prev, [table.id]: true }));

    try {
      const res = await updateTable({
        id: table.id,
        data: {
          code: table.code,
          restaurant: `api/restaurants/${restaurant?.id}`,
          allowOrderPlacement: checked,
        },
      });

      if ("data" in res) {
        toast.success(
          checked
            ? `Commandes activées pour la table ${table.code}`
            : `Commandes désactivées pour la table ${table.code}`
        );
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setLoadingTables((prev) => ({ ...prev, [table.id]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Skeleton className="w-28 h-8" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: limit }).map((_, index) => (
            <Skeleton key={index} className="w-full h-20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        {!isReadOnly && (
          <div className="flex justify-end">
            <ButtonAdd label="Ajouter" type="button" onClick={openModal} />
          </div>
        )}
        <AddTableModal
          item={item}
          isOpen={isOpen}
          onClose={closeModal}
          restaurantId={restaurant?.id}
        />
      </div>
      <TooltipProvider>
        {data?.results && data.results.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.results.map((table) => (
              <div
                key={table.id}
                className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
              >
                <div className="flex justify-between items-start">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-lg font-semibold text-gray-700">
                      {table.code}
                    </h3>
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center">
                            <Switch
                              id={`toggle-${table.id}`}
                              checked={table.allowOrderPlacement ?? true}
                              onChange={(checked) =>
                                handleToggleOrderPlacement(table, checked)
                              }
                              disabled={loadingTables[table.id]}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {loadingTables[table.id]
                              ? "Mise à jour en cours..."
                              : table.allowOrderPlacement ?? true
                              ? "Cliquez pour désactiver les commandes"
                              : "Cliquez pour activer les commandes"}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      {loadingTables[table.id] ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                          <span className="text-sm text-gray-500">
                            Mise à jour...
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-600">
                          {table.allowOrderPlacement ?? true
                            ? "Commandes activées"
                            : "Commandes désactivées"}
                        </span>
                      )}
                    </div>
                  </div>
                  {!isReadOnly && (
                    <div className="flex items-center gap-1">
                      <ButtonEdit onClick={() => openEditModal(table)} />
                      <Delete item={table} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <Armchair className="h-12 w-12 text-muted-foreground opacity-30 mb-3" />
            <p className="text-muted-foreground text-lg mb-2">
              Aucune table
            </p>
            <p className="text-muted-foreground text-sm">
              Cliquez sur "Ajouter" pour commencer
            </p>
          </div>
        )}
      </TooltipProvider>
      {data?.results &&
        data.results.length > 0 &&
        data?.count &&
        (data?.count > limit || limit > 12) && (
          <div className="flex-shrink-0 mt-10">
            <ReactPagination
              itemsPerPage={limit}
              count={data?.count as number}
              onChange={onChange}
              currentPage={page}
              options={[
                { value: 12, label: "12" },
                { value: 24, label: "24" },
                { value: 48, label: "48" },
                { value: 96, label: "96" },
              ]}
            />
          </div>
        )}
    </>
  );
};

export default ListeRestaurantTables;
